// src/search/useSearchFilters.ts
import { useCallback, useState } from "react";
import { useSearchParams } from "react-router-dom";
import type { FacetPatch, SearchFilters, SearchFormState } from "./types";
import { NO_STATUS_VALUE } from "./constants";

/**
 * Check if a value should be kept (not empty/null/undefined).
 */
const keepValue = (v: string | null): v is string =>
  v !== undefined && v !== null && v !== "";

export interface UseSearchFiltersReturn {
  // URL-derived filters
  filters: SearchFilters;
  hasParams: boolean;
  anyFacetActive: boolean;

  // Form state
  formState: SearchFormState;
  setStreetInput: (value: string) => void;
  setPostcodeInput: (value: string) => void;
  setStatusInput: (value: string[]) => void;
  isFormPristine: boolean;
  searchAllowed: boolean;

  // Actions
  handleSearch: () => void;
  handleClear: () => void;
  updateFacets: (patch: FacetPatch) => void;
  clearAllFacets: () => void;
  applyFilters: (street: string, postcode: string, status: string[]) => void;
}

export function useSearchFilters(): UseSearchFiltersReturn {
  const [searchParams, setSearchParams] = useSearchParams();

  // Parse URL params into filters
  const street = searchParams.get("street") ?? "";
  const postcode = searchParams.get("postcode") ?? "";
  const status = searchParams.get("status") ?? "";
  const statusList = status ? status.split(",").filter(Boolean) : [];

  const postcodeFacet =
    searchParams.get("postcode_facet")?.split(",").filter(keepValue) ?? [];
  const cityFacet =
    searchParams.get("post_town_facet")?.split(",").filter(keepValue) ?? [];
  const localityFacet =
    searchParams.get("dependent_locality_facet")?.split(",").filter(keepValue) ?? [];
  const statusFacet =
    searchParams.get("status_facet")?.split(",").filter(keepValue) ?? [];

  const filters: SearchFilters = {
    street,
    postcode,
    statusList,
    postcodeFacet,
    cityFacet,
    localityFacet,
    statusFacet,
  };

  const hasParams =
    searchParams.has("street") ||
    searchParams.has("postcode") ||
    searchParams.has("postcode_facet") ||
    searchParams.has("post_town_facet") ||
    searchParams.has("dependent_locality_facet") ||
    searchParams.has("status_facet") ||
    searchParams.has("status");

  const anyFacetActive =
    postcodeFacet.length > 0 ||
    cityFacet.length > 0 ||
    localityFacet.length > 0 ||
    statusFacet.length > 0;

  // Form state (local inputs before search is triggered)
  const [streetInput, setStreetInput] = useState(street);
  const [postcodeInput, setPostcodeInput] = useState(postcode);
  const [statusInput, setStatusInput] = useState<string[]>(statusList);

  const formState: SearchFormState = {
    streetInput,
    postcodeInput,
    statusInput,
  };

  const isFormPristine =
    streetInput.trim() === "" &&
    postcodeInput.trim() === "" &&
    statusInput.length === 0;

  const searchAllowed =
    streetInput.trim() !== "" ||
    postcodeInput.trim() !== "" ||
    (statusInput.length > 0 &&
      !(statusInput.length === 1 && statusInput[0] === NO_STATUS_VALUE));

  /**
   * Apply current form state to URL params (triggers search).
   */
  const handleSearch = useCallback(() => {
    if (!searchAllowed) return;

    const params = new URLSearchParams();
    const trimmedStreet = streetInput.trim();
    const trimmedPostcode = postcodeInput.trim();

    if (trimmedStreet) params.set("street", trimmedStreet);
    if (trimmedPostcode) params.set("postcode", trimmedPostcode);
    if (statusInput.length) params.set("status", statusInput.join(","));

    setSearchParams(params);
  }, [searchAllowed, streetInput, postcodeInput, statusInput, setSearchParams]);

  /**
   * Apply specific filter values to URL params.
   */
  const applyFilters = useCallback(
    (newStreet: string, newPostcode: string, newStatus: string[]) => {
      setStreetInput(newStreet);
      setPostcodeInput(newPostcode);
      setStatusInput(newStatus);

      const params = new URLSearchParams();
      if (newStreet.trim()) params.set("street", newStreet.trim());
      if (newPostcode.trim()) params.set("postcode", newPostcode.trim());
      if (newStatus.length) params.set("status", newStatus.join(","));

      setSearchParams(params);
    },
    [setSearchParams]
  );

  /**
   * Clear all filters and form state.
   */
  const handleClear = useCallback(() => {
    setStreetInput("");
    setPostcodeInput("");
    setStatusInput([]);
    setSearchParams({});
  }, [setSearchParams]);

  /**
   * Update facet filters in URL.
   */
  const updateFacets = useCallback(
    (patch: FacetPatch) => {
      setSearchParams((prev) => {
        const params = new URLSearchParams(prev);

        if (patch.postcode_facet !== undefined) {
          if (patch.postcode_facet.length) {
            params.set("postcode_facet", patch.postcode_facet.join(","));
          } else {
            params.delete("postcode_facet");
          }
        }

        if (patch.post_town_facet !== undefined) {
          if (patch.post_town_facet.length) {
            params.set("post_town_facet", patch.post_town_facet.join(","));
          } else {
            params.delete("post_town_facet");
          }
        }

        if (patch.dependent_locality_facet !== undefined) {
          if (patch.dependent_locality_facet.length) {
            params.set("dependent_locality_facet", patch.dependent_locality_facet.join(","));
          } else {
            params.delete("dependent_locality_facet");
          }
        }

        if (patch.status_facet !== undefined) {
          if (patch.status_facet.length) {
            params.set("status_facet", patch.status_facet.join(","));
          } else {
            params.delete("status_facet");
          }
        }

        return params;
      });
    },
    [setSearchParams]
  );

  /**
   * Clear all facet filters.
   */
  const clearAllFacets = useCallback(() => {
    if (!anyFacetActive) return;
    updateFacets({
      postcode_facet: [],
      post_town_facet: [],
      dependent_locality_facet: [],
      status_facet: [],
    });
  }, [anyFacetActive, updateFacets]);

  return {
    filters,
    hasParams,
    anyFacetActive,
    formState,
    setStreetInput,
    setPostcodeInput,
    setStatusInput,
    isFormPristine,
    searchAllowed,
    handleSearch,
    handleClear,
    updateFacets,
    clearAllFacets,
    applyFilters,
  };
}
